import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepCompanyProps {
  onNext: () => void;
}

export function StepCompany({ onNext }: StepCompanyProps) {
  const { formData, updateFormData, error } = useRegistrationStore();
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const validate = () => {
    const errors: Record<string, string> = {};
    const name = formData.companyName.trim();

    if (!name) {
      errors.companyName = t.errorCompanyNameRequired;
    } else if (name.length < 2) {
      errors.companyName = t.errorCompanyNameMinLength;
    } else if (name.length > 50) {
      errors.companyName = t.errorCompanyNameMaxLength;
    } else if (/[<>/\\"`;|{}\[\]]/.test(name)) {
      errors.companyName = t.errorCompanyNameInvalidChars;
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      // Trim whitespace before proceeding — backend expects clean input
      const trimmed = formData.companyName.trim();
      if (trimmed !== formData.companyName) {
        updateFormData({ companyName: trimmed });
      }
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {t.whatsYourCompanyCalled}
        </h2>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="companyName">{t.companyName}</Label>
          <Input
            id="companyName"
            value={formData.companyName}
            onChange={(e) => updateFormData({ companyName: e.target.value })}
            placeholder={t.companyNamePlaceholder}
            className={`h-12 rounded-md !border-border !bg-background ${localErrors.companyName ? 'border-destructive' : ''}`}
            autoFocus
          />
          {localErrors.companyName && (
            <p className="text-sm text-destructive">{localErrors.companyName}</p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}

      <Button onClick={handleNext} className="w-full h-12 rounded-md text-base">
        {t.next}
      </Button>
    </div>
  );
}
